/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useTheme from '../../../utils/hooks/useTheme';

// Component imports
import { Button, Col, Form, Row, Select, Slider, Space, Switch, Typography } from 'antd';
import RichTextEditor from '../Fields/RichTextEditor/RichTextEditor';
import Prompt from '../../UI/Prompt';
import { InfoCircleOutlined } from '@ant-design/icons';

// Misc imports
import { INTEGRATION_CATEGORY, INTEGRATION_TYPE, PLUGIN_CATEGORY } from '../../../constants/integrations';
import { useFetchBotDefaultPromptQuery, useFetchBotsQuery, useUpdateBotMutation } from '../../../store/bots/botsApi';
import { getCurrentQueryState } from '../../../store/utils/getCurrentQueryState';
import { OPEN_AI_COMPLETION_MODELS, GEMINI_MODELS, SESSION_ANALYSIS_MODES, LANGUAGE_DETECTION_EXPERT_MODE_DEFAULT_INITIAL_VALUE } from '../../../constants/main';
import openConsoleAlertMessage from '../../../utils/misc/openConsoleAlertMessage';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../constants/markdown';

const PluginLanguageDetectionForm = ({
  form,
  onSuccess,
  onCanSubmitChange,
  onLoadingChange,
  isPreActivation = false,
  pendingIntegrationType = null
}) => {
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { formProps, borderRadiusBase } = useTheme();
  const { data: fetchedBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: bots } = useFetchBotsQuery({ orgId, urlParams: { includePlugins: true } }, { skip: !orgId || tenantId });
  const { data: botDefaultPrompt } = useFetchBotDefaultPromptQuery({ orgId, category: PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION }, { skip: !orgId || !tenantId });
  const bot = useMemo(() => fetchedBot || (bots?.items || []).find(bot => bot.id === form.getFieldValue('bot')), [fetchedBot, bots, form.getFieldValue('bot')]);
  const maxHistoryTurnsSliderRef = useRef(null);

  const languageDetectionPlugin = useMemo(() => (bot?.plugins || []).find(plugin => plugin.integration.category === INTEGRATION_CATEGORY.LLM && plugin.category === PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION), [bot]);
  const [, updateState] = useState();
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();
  const [canSubmit, setCanSubmit] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [localModels, setLocalModels] = useState([]);

  const isGeminiIntegration = (languageDetectionPlugin?.integration?.type || pendingIntegrationType) === INTEGRATION_TYPE.GEMINI_AI_LLM_LANGUAGE_DETECTION;
  const isAzureIntegration = (languageDetectionPlugin?.integration?.type || pendingIntegrationType) === INTEGRATION_TYPE.AZURE_AI_LLM_LANGUAGE_DETECTION;

  const currentModelListToBeUsed = useMemo(() => {
    return isGeminiIntegration ? GEMINI_MODELS : OPEN_AI_COMPLETION_MODELS;
  }, [isGeminiIntegration]);

  useEffect(() => {
    form.resetFields();
  }, [fetchedBot?.id]);

  useEffect(() => {
    setLocalModels(Object.values(currentModelListToBeUsed));
  }, []);

  const updateModels = (newModelName) => {
    if (newModelName && Object.values(currentModelListToBeUsed).filter(model => model === newModelName).length === 0) {
      setLocalModels([...new Set([...Object.values(currentModelListToBeUsed), newModelName])]);
    }
  };

  useEffect(() => {
    // Reset the form fields when the bot changes (except for the bot field itself)
    form.resetFields(Object.keys(form.getFieldsValue()).filter(field => field !== 'bot'));
  }, [bot?.id]);

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isUpdatingBot);
    }
  }, [isUpdatingBot]);

  const historyValue = Form.useWatch('history', form);

  useEffect(() => {
  // When historyValue changes (from user interaction)
  // then programmatically update the *other* field.
    if (historyValue) {
      form.setFieldValue('maxHistoryTurns', 8);
    } else {
      form.setFieldValue('maxHistoryTurns', 0);
    }
  // We only want this to run when 'historyValue' changes
  }, [historyValue, form]);

  const updateCanSubmit = async () => {
    try {
      await form.validateFields();
      setCanSubmit(true);
      if (onCanSubmitChange) {
        onCanSubmitChange(true);
      }
    } catch (e) {
      const formHasErrors = e.errorFields?.length > 0;
      setCanSubmit(!formHasErrors);
      if (onCanSubmitChange) {
        onCanSubmitChange(!formHasErrors);
      }
    }
  };

  const onSubmit = async (values) => {
    if (!languageDetectionPlugin && !isPreActivation) {
      openConsoleAlertMessage({ type: 'error', message: t('The agent does not have the required plugin enabled.', { context: 'AI agent' }) });
      return;
    }

    let isSubmitSuccessful = true;

    if (settingsChanged) {

      let valuesToSubmit = { ...values };

      if(!valuesToSubmit?.history) {
        valuesToSubmit.maxHistoryTurns = 0;
      }
      delete valuesToSubmit.history;

      if(!isPreActivation) {
        const newPlugins = bot.plugins.map(plugin => {
          return {
            integrationId: plugin.integration.id,
            settings: plugin.integration.id === languageDetectionPlugin.integration.id && plugin.category === PLUGIN_CATEGORY.LLM_LANGUAGE_DETECTION
              ? {
                ...plugin.settings,
                ...valuesToSubmit
              }
              : plugin.settings,
            category: plugin.category
          };
        });

        isSubmitSuccessful = await updateBot({ orgId, tenantId: bot?.id, data: { plugins: newPlugins } }).unwrap();
      }
    }

    if (isSubmitSuccessful) {
      setCanSubmit(false);
      setSettingsChanged(false);
      if (onSuccess) {
        onSuccess(values);
      }
    }
  };
  return (
    <Fragment>
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        onValuesChange={(changedValue, allFields) => {
          const fieldIsBot = changedValue.bot;

          if(Reflect.has(changedValue, 'mode')) {
            const currentModeStringValue = changedValue.mode ? SESSION_ANALYSIS_MODES.EXPERT : SESSION_ANALYSIS_MODES.BASIC;
            form.setFieldValue('mode', currentModeStringValue);
          }
          if (!fieldIsBot) {
            setSettingsChanged(true);
            (async () => {
              await updateCanSubmit();
            })();
          }
          updateState(allFields);
        }}
        initialValues={{
          ...languageDetectionPlugin?.settings,
          mode: languageDetectionPlugin?.settings?.mode || SESSION_ANALYSIS_MODES.BASIC,
          prompt: languageDetectionPlugin?.settings?.prompt || botDefaultPrompt?.prompt || LANGUAGE_DETECTION_EXPERT_MODE_DEFAULT_INITIAL_VALUE.prompt,
          maxHistoryTurns: languageDetectionPlugin?.settings?.maxHistoryTurns > 0 ? languageDetectionPlugin?.settings?.maxHistoryTurns : 8,
          history: languageDetectionPlugin?.settings?.maxHistoryTurns > 0
        }}
      >
        <div className="position-absolute" style={{ top: 15, right: 30 }}>
          <Space direction="horizontal">
            <Typography.Text>
              {t('Expert Mode')}
            </Typography.Text>
            <Form.Item
              className="m-0 label-p-0"
              name="mode"
            >
              <Switch checked={form.getFieldValue('mode') === SESSION_ANALYSIS_MODES.EXPERT} className="float-right" />
            </Form.Item>
          </Space>
        </div>
        <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan}>
            <Typography.Text>
              {t('LLM Configuration')}
            </Typography.Text>
            <Typography.Paragraph className="fs-sm" type="secondary">
              {t(`Select the desired LLM model and adjust the advanced language-detection settings. Based on user messages, the Agent identifies the appropriate response language and updates the language system variable accordingly.`)}
            </Typography.Paragraph>
          </Col>
          <Col xs={formProps.secondaryColSpan}>

            <Space direction="vertical" className="w-100 gap-4">
              <Form.Item
                required
                name={['model']}
                rules={[
                  {
                    required: true,
                    message: t('Field cannot be blank.')
                  }]}
                label={isAzureIntegration ? t('Deployment') : t('Model') }
                className="mb-0"
              >
                <Select
                  showSearch
                  onSearch={updateModels}
                  onBlur={(e) => updateModels(e.target.value)}
                  placeholder={t('Type / Select model')}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                  options={(localModels || [])
                    .map((element) => {
                      return {
                        value: element,
                        label: element
                      };
                    })
                  // Keep only unique values
                    .filter((elem, index, self) => self.findIndex((t) => {
                      return (t.value === elem.value);
                    }) === index)
                  }
                />
              </Form.Item>
              <Form.Item
                hidden={form.getFieldValue('mode') === SESSION_ANALYSIS_MODES.BASIC}
                label={t('Prompt')}
                name={'prompt'}
                className="mb-0"
                required
                rules={[
                  {
                    required: true,
                    message: t('Prompt cannot be blank.')
                  }
                ]}
              >
                <RichTextEditor
                  toolbarActions={[
                    RICH_TEXT_TOOLBAR_ACTIONS.HEADING,
                    RICH_TEXT_TOOLBAR_ACTIONS.BOLD,
                    RICH_TEXT_TOOLBAR_ACTIONS.ITALIC,
                    RICH_TEXT_TOOLBAR_ACTIONS.BULLET_LIST,
                    RICH_TEXT_TOOLBAR_ACTIONS.ORDERED_LIST
                  ]}
                  shouldAutoFocus={false}
                  copySourceButton
                  style={{ height: '260px', minHeight: '260px' }}
                  placeholder={t('Enter the prompt')}
                  elementStyles={{ p: { marginBottom: 0 } }}
                />
              </Form.Item>

              <Form.Item
                hidden={form.getFieldValue('mode') === SESSION_ANALYSIS_MODES.BASIC}
                label="Include History"
                name={'history'}
                valuePropName="checked"
                className="mb-0"
                tooltip={{
                  icon: <InfoCircleOutlined />,
                  title: (
                    <Typography.Text type="secondary">{t('Include previous user messages in language detection. Always counts at least the last message.')}</Typography.Text>
                  )
                }}
              >
                <Switch />
              </Form.Item>

              <div ref={maxHistoryTurnsSliderRef}>
                <Form.Item
                  label={t('Max Messages')}
                  name={'maxHistoryTurns'}
                  dependencies={['history']}
                  className="mb-0"
                  hidden={form.getFieldValue('mode') === SESSION_ANALYSIS_MODES.BASIC || !form.getFieldValue('history')}
                  tooltip={{
                    icon: <InfoCircleOutlined />,
                    title: (
                      <Typography.Text type="secondary">{t('Set how many previous messages (in addition to the current one) are included in language detection.')}</Typography.Text>
                    )
                  }}
                >
                  <Slider
                    min={1}
                    max={15}
                    step={1}
                    className="custom-slider"
                    marks={{ 1: '1', 8: 'Default', 15: '15' }}
                    handleStyle={{ borderRadius: borderRadiusBase }}
                    onChange={(value) => {
                      const sliderHandler = maxHistoryTurnsSliderRef.current.querySelector('.ant-slider-handle');
                      if (!sliderHandler) {
                        return;
                      }
                      sliderHandler.innerHTML = value;
                    }}
                  />
                </Form.Item>
              </div>
            </Space>
          </Col>
        </Row>
      </Form>

      <Prompt
        when={canSubmit}
        onConfirm={() => form.submit()}
        okText={t('Save Changes')}
        cancelText={t('Quit without saving')}
        title={t('Save Changes?')}
        message={t('Your unsaved changes will be lost. Save changes before closing?')}
      />

      {!onSuccess ? (
        <Row justify="end" className="mt-4">
          <Col>
            <Button
              type="primary"
              size="large"
              onClick={() => form.submit()}
              disabled={!canSubmit}
            >
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>
      ) : null}
    </Fragment>
  );
};

PluginLanguageDetectionForm.propTypes = {
  form: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onCanSubmitChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  isPreActivation: PropTypes.bool,
  pendingIntegrationType: PropTypes.string
};

export default PluginLanguageDetectionForm;
